import { DataProvider, GetListParams, QueryFunctionContext } from "react-admin";
import userProvider from "./usersProvider";
import entityProvider from "./entitiesProvider";
import voucherProvider from "./vouchersProvider";
import locationProvider from "./locationsProvider";
import giftcardsProvider from "./giftcardsProvider";
import productsOfferedProvider from "./productsOfferedProvider";
import bogoProvider from "./bogoProvider";
import discountsProvider from "./discountsProvider";
import citiesProvider from "./citiesProvider";
import entitieslocationProvider from "./entitiesLocationProvider";
import { Voucher, VoucherStats } from "@/types";
import consumeProvider from "./consumeProvider";
import statProvider, { StatProvider } from "./statProvider";
import cashierProvider from "./cashierProvider";

type Resource =
  | "users"
  | "entities"
  | "vouchers"
  | "locations"
  | "giftcards"
  | "productsoffered"
  | "bogo"
  | "discount"
  | "cities"
  | "entitieslocation"
  | "stats"
  | "cashier";

type ExtendedDataProvider = DataProvider & {
  consumeVoucher: (args: {
    code: string;
    pseudo: string;
    invoice_amount: number;
  }) => Promise<{ data: Voucher }>;
  getVoucherStats?: () => Promise<{ data: VoucherStats }>;
};

const providersMap: Record<Resource, Partial<DataProvider>> = {
  users: userProvider,
  entities: entityProvider,
  vouchers: voucherProvider,
  locations: locationProvider,
  giftcards: giftcardsProvider,
  productsoffered: productsOfferedProvider,
  bogo: bogoProvider,
  discount: discountsProvider,
  cities: citiesProvider,
  entitieslocation: entitieslocationProvider,
  stats: statProvider,
  cashier: cashierProvider,
};

const ensureProviderMethodExists = (
  provider: Partial<DataProvider>,
  methodName: keyof DataProvider,
  resource: string
): void => {
  if (!(methodName in provider)) {
    throw new Error(
      `Unknown resource or missing method: ${resource} -> ${methodName}`
    );
  }
};

const callProviderMethod = async (
  methodName: keyof DataProvider,
  resource: string,
  params: GetListParams & QueryFunctionContext
) => {
  try {
    const provider = providersMap[resource as keyof typeof providersMap];
    ensureProviderMethodExists(provider, methodName, resource);
    return provider[methodName](resource, params);
  } catch (error) {
    console.error("DataProvider error:", error);
    throw error;
  }
};

const dataProvider: ExtendedDataProvider = {
  getList: async (resource, params) => {
    return callProviderMethod("getList", resource, params);
  },

  getOne: async (resource, params) =>
    callProviderMethod("getOne", resource, params),

  create: async (resource, params) =>
    callProviderMethod("create", resource, params),

  update: async (resource, params) =>
    callProviderMethod("update", resource, params),

  delete: async (resource, params) =>
    callProviderMethod("delete", resource, params),

  getMany: async (resource, params) =>
    callProviderMethod("getMany", resource, params),

  getManyReference: async (resource, params) =>
    callProviderMethod("getManyReference", resource, params),

  updateMany: async (resource, params) =>
    callProviderMethod("updateMany", resource, params),

  deleteMany: async (resource, params) =>
    callProviderMethod("deleteMany", resource, params),

  async consumeVoucher({ code, pseudo, invoice_amount }) {
    return consumeProvider.consumeVoucher({ code, pseudo, invoice_amount });
  },

  async getVoucherStats() {
    return consumeProvider.getVoucherStats();
  },

  async getHistoryVoucher() {
    const p = providersMap["stats"] as StatProvider;
    if (!p?.getHistoryVoucher) {
      throw new Error("Unknown dataProvider function: getHistoryVoucher");
    }
    return p.getHistoryVoucher();
  },
};

export { dataProvider };
